import React, { useRef, useState, useCallback } from "react";
import {
  TbArrowLeft, TbArrowBackUp, TbArrowForwardUp, TbAdjustmentsHorizontal, TbSparkles,
  TbDeviceFloppy, TbChevronDown, TbDownload, TbChartBar, TbComponents, TbBookmark,
  TbLayoutSidebarRight, TbGridDots, TbZoomIn, TbZoomOut, TbClick, TbX, TbCopyPlus,
  TbTrash, TbLock,
} from "react-icons/tb";
import { useDismissable, returnFocus } from "../../../v2/hooks/useDismissable";
import "../../../v2/styles/tokens.css";
import "../../../v2/components/primitives.css";
import "./BuilderV2.css";
import "./BuilderV2.dark.css";

// The chrome around the report builder in the new interface.
//
// The builder itself (state, canvas, widgets, export) is shared by both interfaces and
// lives in ReportBuilder.jsx. Only the frame changes: v1 stacks two toolbars of emoji
// buttons over the canvas with slide-over drawers; v2 is an application frame — one
// header for document actions, an icon rail for the content library, a canvas bar for
// view controls, a docked inspector, and a status bar.
//
// Everything here is presentational. ReportBuilder.jsx owns the state and hands down
// handlers, so v1 and v2 can never disagree about what a button does.
//
// The palette comes from .v2-tokens on the builder root (plus .dark when the user's theme
// is dark). Nothing here carries .v2 itself: that class re-declares the light palette on
// the element, which would pin these bars to light under a dark root. See .v2-tokens in
// tokens.css for why the root does not use .v2 either.

const Divider = () => <span className="rb2-divider" aria-hidden="true" />;

const IconButton = ({ label, shortcut, icon: Icon, active, className = "", ...rest }) => (
  <button
    type="button"
    className={`rb2-iconbtn${active ? " on" : ""} ${className}`}
    aria-label={label}
    aria-pressed={active === undefined ? undefined : !!active}
    title={shortcut ? `${label} (${shortcut})` : label}
    {...rest}
  >
    <Icon size={17} />
  </button>
);

// Save, with the two less common template actions behind a caret. A split button
// rather than a menu so the frequent action stays one click.
const SaveSplit = ({ saving, onSave, onSaveAs, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const close = useCallback(() => { returnFocus(ref, triggerRef); setOpen(false); }, []);
  useDismissable(open, { ref, triggerRef, onClose: close });

  return (
    <div className="rb2-split">
      <button type="button" className="v2-btn rb2-split-main" onClick={onSave} disabled={saving}>
        <TbDeviceFloppy size={15} /> {saving ? "Saving…" : "Save"}
      </button>
      <button
        type="button" ref={triggerRef} className="v2-btn rb2-split-caret"
        aria-label="More save options" aria-haspopup="menu" aria-expanded={open}
        disabled={saving} onClick={() => setOpen((o) => !o)}
      >
        <TbChevronDown size={14} />
      </button>
      {open && (
        <div className="rb2-menu" role="menu" ref={ref}>
          <button type="button" role="menuitem" onClick={() => { setOpen(false); onSaveAs(); }}>
            <TbCopyPlus size={15} /> Save as new template…
          </button>
          <div className="rb2-menu-sep" role="separator" />
          <button type="button" role="menuitem" className="danger" onClick={() => { setOpen(false); onDelete(); }}>
            <TbTrash size={15} /> Delete template
          </button>
        </div>
      )}
    </div>
  );
};

export const BuilderHeaderV2 = ({
  section, title, onBack, backLabel,
  isTemplateMode, readOnly,
  canUndo, canRedo, onUndo, onRedo,
  onOpenSettings,
  onAI, aiLabel,
  showSave, currentTemplateId, saving, onSave, onSaveAs, onDelete,
  onExport,
}) => (
  <header className="rb2-header">
    <IconButton label={backLabel} icon={TbArrowLeft} onClick={onBack} />
    <Divider />
    <nav className="v2-crumbs rb2-crumbs" aria-label="Breadcrumb">
      <span>{section}</span>
      <span aria-hidden="true">/</span>
      <b title={title}>{title}</b>
    </nav>
    {isTemplateMode && (
      <span className="v2-rbadge v2-rbadge--accent" title="Charts are filled with sample data while you design the layout">
        Template · sample data
      </span>
    )}
    {readOnly && (
      <span className="v2-rbadge v2-rbadge--warning" title="Editing the report layout is available on Pro">
        <TbLock size={11} /> View only
      </span>
    )}

    <div className="rb2-header-actions">
      {!readOnly && (
        <>
          <IconButton label="Undo" shortcut="Ctrl+Z" icon={TbArrowBackUp} onClick={onUndo} disabled={!canUndo} />
          <IconButton label="Redo" shortcut="Ctrl+Shift+Z" icon={TbArrowForwardUp} onClick={onRedo} disabled={!canRedo} />
          <Divider />
          <IconButton label="Report settings — status colours, page numbers" icon={TbAdjustmentsHorizontal} onClick={onOpenSettings} />
          {onAI && (
            <button type="button" className="v2-btn rb2-ai" onClick={onAI}>
              <TbSparkles size={15} /> {aiLabel}
            </button>
          )}
          {showSave && (currentTemplateId ? (
            <SaveSplit saving={saving} onSave={onSave} onSaveAs={onSaveAs} onDelete={onDelete} />
          ) : (
            <button type="button" className="v2-btn" onClick={onSaveAs} disabled={saving}>
              <TbDeviceFloppy size={15} /> {saving ? "Saving…" : "Save template"}
            </button>
          ))}
        </>
      )}
      {onExport && (
        <button type="button" className="v2-btn v2-btn--primary" onClick={onExport}>
          <TbDownload size={15} /> Export
        </button>
      )}
    </div>
  </header>
);

// The library rail. Selecting the open panel's own icon closes it, the same way the
// sidebar rail in the v2 shell behaves.
export const LIBRARY_ITEMS = [
  { key: "widgets", label: "Widgets", icon: TbChartBar },
  { key: "elements", label: "Elements", icon: TbComponents },
  { key: "custom", label: "Saved", icon: TbBookmark },
];

export const BuilderRailV2 = ({ items, activeKey, open, onSelect }) => (
  <nav className="rb2-rail" aria-label="Content library">
    {items.map(({ key, label, icon: Icon, count }) => {
      const on = open && activeKey === key;
      return (
        <button
          key={key} type="button"
          className={`rb2-railbtn${on ? " on" : ""}`}
          aria-pressed={on}
          title={on ? `Hide ${label.toLowerCase()}` : label}
          onClick={() => onSelect(key)}
        >
          <Icon size={19} />
          <span>{label}</span>
          {count > 0 && <i className="rb2-railbtn-n">{count}</i>}
        </button>
      );
    })}
  </nav>
);

// View controls for the canvas: page navigation on the left, how the page is shown on
// the right. Nothing here changes the report.
export const CanvasBarV2 = ({
  pageNav, readOnly,
  orientation, onOrientationToggle,
  showGrid, onToggleGrid,
  zoomLevel, onZoomIn, onZoomOut, onZoomReset,
  propertiesVisible, onToggleProperties,
}) => (
  <div className="rb2-canvasbar">
    <div className="rb2-canvasbar-pages">{pageNav}</div>

    <div className="rb2-canvasbar-view">
      {!readOnly && (
        <div className="v2-seg" role="group" aria-label="Page orientation">
          {["portrait", "landscape"].map((o) => (
            <button
              key={o} type="button" className={orientation === o ? "on" : ""}
              aria-pressed={orientation === o}
              onClick={() => orientation !== o && onOrientationToggle()}
            >
              {o === "portrait" ? "Portrait" : "Landscape"}
            </button>
          ))}
        </div>
      )}
      <Divider />
      <IconButton label="Show grid" icon={TbGridDots} active={showGrid} onClick={onToggleGrid} />
      <div className="rb2-zoom" role="group" aria-label="Zoom">
        <IconButton label="Zoom out" icon={TbZoomOut} onClick={onZoomOut} disabled={zoomLevel <= 0.25} />
        <button type="button" className="rb2-zoom-val" title="Reset zoom to 100%" onClick={onZoomReset}>
          {Math.round(zoomLevel * 100)}%
        </button>
        <IconButton label="Zoom in" icon={TbZoomIn} onClick={onZoomIn} disabled={zoomLevel >= 2} />
      </div>
      {!readOnly && (
        <>
          <Divider />
          <IconButton label="Inspector" icon={TbLayoutSidebarRight} active={propertiesVisible} onClick={onToggleProperties} />
        </>
      )}
    </div>
  </div>
);

// What the docked inspector shows before anything is selected. Without it the panel is
// a blank column and reads as broken.
export const InspectorEmptyV2 = ({ onClose }) => (
  <div className="rb2-inspector-empty">
    <div className="rb2-panel-head">
      <span>Inspector</span>
      <IconButton label="Close inspector" icon={TbX} onClick={onClose} />
    </div>
    <div className="rb2-inspector-empty-body">
      <TbClick size={26} />
      <b>Nothing selected</b>
      <p>Select a widget or element on the page to edit its content, data and style.</p>
    </div>
  </div>
);

export const BuilderStatusBarV2 = ({ left = [], right = [] }) => (
  <footer className="rb2-status">
    <div className="rb2-status-group">
      {left.filter(Boolean).map((item, i) => <span key={i}>{item}</span>)}
    </div>
    <div className="rb2-status-group">
      {right.filter(Boolean).map((item, i) => <span key={i}>{item}</span>)}
    </div>
  </footer>
);
